import React, { useState } from 'react';
import { FiLayers, FiArrowDown, FiServer, FiUsers, FiActivity, FiLock, FiTarget, FiChevronDown, FiLoader, FiCpu, FiBook, FiCode, FiZap, FiCheckCircle, FiAlertTriangle, FiClock, FiX, FiClipboard, FiGlobe, FiArrowRight } from 'react-icons/fi';
import { getStageRecommendation } from '../api';

const cardStyle = {
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #e5e7eb',
  padding: '1.5rem',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};

const PROVIDER_META = {
  azure: { label: 'Microsoft Azure', shortLabel: 'Azure', color: '#1e40af', bg: '#dbeafe', border: '#bfdbfe', serviceKey: 'cloudService', tagKey: 'cloudService', framework: 'Microsoft Cloud Adoption Framework (CAF)' },
  aws: { label: 'Amazon Web Services', shortLabel: 'AWS', color: '#9a3412', bg: '#ffedd5', border: '#fed7aa', serviceKey: 'cloudService', tagKey: 'cloudService', framework: 'AWS Control Tower & Well-Architected Framework' },
  gcp: { label: 'Google Cloud Platform', shortLabel: 'GCP', color: '#065f46', bg: '#dcfce7', border: '#bbf7d0', serviceKey: 'cloudService', tagKey: 'cloudService', framework: 'GCP Cloud Foundation Toolkit' },
};

const ARCH_LAYERS = [
  {
    key: 'identity', label: 'Identity & Access', icon: FiUsers,
    color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe',
    items: { azure: ['Entra ID / Azure AD', 'RBAC Role Assignments', 'Conditional Access Policies', 'Privileged Identity Management'], aws: ['AWS IAM', 'IAM Roles & Policies', 'AWS SSO / Identity Center', 'Service Control Policies'], gcp: ['Cloud Identity', 'IAM Roles & Bindings', 'Organization Policies', 'Workload Identity'] },
    categoryIndex: 4, questionIndices: [20, 21],
  },
  {
    key: 'management', label: 'Management Structure', icon: FiLayers,
    color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe',
    items: { azure: ['Tenant Root Group', 'Management Group Hierarchy', 'Subscription Organization', 'Resource Group Strategy'], aws: ['AWS Organizations', 'Organizational Units (OUs)', 'Account Strategy', 'Resource Groups & Tags'], gcp: ['Organization Node', 'Folder Hierarchy', 'Project Strategy', 'Labels & Tags'] },
    categoryIndex: 2, questionIndices: [10, 11, 13, 14],
  },
  {
    key: 'networking', label: 'Networking & Connectivity', icon: FiServer,
    color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc',
    items: { azure: ['Hub Virtual Network', 'Spoke VNets', 'Azure Firewall / NVA', 'ExpressRoute / VPN Gateway', 'DNS Zones'], aws: ['Transit Gateway', 'VPCs', 'AWS Network Firewall', 'Direct Connect / Site-to-Site VPN', 'Route 53'], gcp: ['Shared VPC', 'VPC Networks', 'Cloud Firewall', 'Cloud Interconnect / VPN', 'Cloud DNS'] },
    categoryIndex: 2, questionIndices: [12, 23],
  },
  {
    key: 'governance', label: 'Governance & Policy', icon: FiTarget,
    color: '#d97706', bg: '#fffbeb', border: '#fde68a',
    items: { azure: ['Azure Policy Assignments', 'Cost Management & Budgets', 'Tagging Standards', 'Resource Lifecycle', 'Compliance Mapping'], aws: ['Service Control Policies', 'AWS Budgets & Cost Explorer', 'Tag Policies', 'Config Rules', 'Compliance Packs'], gcp: ['Organization Policies', 'Cloud Billing Budgets', 'Label Policies', 'Resource Manager', 'Security Command Center'] },
    categoryIndex: 3, questionIndices: [15, 16, 17, 18, 19],
  },
  {
    key: 'security', label: 'Security & Compliance', icon: FiLock,
    color: '#dc2626', bg: '#fef2f2', border: '#fecaca',
    items: { azure: ['Key Vault', 'Microsoft Defender for Cloud', 'Security Baselines', 'Network Segmentation', 'Threat Detection'], aws: ['AWS Secrets Manager / KMS', 'AWS Security Hub', 'Security Baselines', 'Security Groups / NACLs', 'GuardDuty'], gcp: ['Secret Manager / Cloud KMS', 'Security Command Center', 'Security Baselines', 'VPC Firewall Rules', 'Chronicle / Threat Detection'] },
    categoryIndex: 4, questionIndices: [21, 22, 24],
  },
  {
    key: 'operations', label: 'Operations & Monitoring', icon: FiActivity,
    color: '#059669', bg: '#f0fdf4', border: '#bbf7d0',
    items: { azure: ['Azure Monitor / Log Analytics', 'Alerting & Dashboards', 'Backup & DR', 'Auto-scaling', 'SLA/SLO Tracking'], aws: ['CloudWatch / CloudTrail', 'SNS Alerts & Dashboards', 'AWS Backup', 'Auto Scaling Groups', 'Trusted Advisor'], gcp: ['Cloud Monitoring / Logging', 'Alerting & Dashboards', 'Backup & DR', 'Autoscaler', 'SLO Monitoring'] },
    categoryIndex: 5, questionIndices: [25, 26, 27, 28, 29],
  },
];

const FLOW_STEPS = [
  { label: 'Discovery Workshop', sublabel: 'Capture requirements & readiness', color: '#4f46e5', icon: FiTarget },
  { label: 'Landing Zone Design', sublabel: 'Architecture & blueprint definition', color: '#7c3aed', icon: FiLayers },
  { label: 'Identity & Access Setup', sublabel: 'Identity provider, RBAC, privileged access', color: '#7c3aed', icon: FiUsers },
  { label: 'Networking Foundation', sublabel: 'Network topology, firewall, DNS, connectivity', color: '#0891b2', icon: FiServer },
  { label: 'Governance & Policy Setup', sublabel: 'Policy assignments, tagging, cost management', color: '#d97706', icon: FiTarget },
  { label: 'Security Baseline', sublabel: 'Security controls, key management, threat detection', color: '#dc2626', icon: FiLock },
  { label: 'Workload Onboarding', sublabel: 'Workload deployment & app migration', color: '#2563eb', icon: FiServer },
  { label: 'Monitoring & Operations', sublabel: 'Observability, DR, alerting, ITSM', color: '#059669', icon: FiActivity },
];

const TAB_DEFS = [
  { key: 'overview', label: 'Overview', icon: FiBook },
  { key: 'guide', label: 'Implementation Guide', icon: FiClipboard },
  { key: 'architecture', label: 'Architecture Reference', icon: FiGlobe },
  { key: 'terraform', label: 'Terraform Accelerator', icon: FiCode },
  { key: 'ai', label: 'AI Recommendations', icon: FiCpu },
];

function getLayerMaturity(answers, layer) {
  const indices = layer.questionIndices;
  if (!indices || indices.length === 0) return 0;
  let sum = 0, count = 0;
  indices.forEach(idx => {
    if (answers[idx] !== undefined) { sum += answers[idx]; count++; }
  });
  if (count === 0) return 0;
  return Math.round((sum / (count * 5)) * 100);
}

function getMaturityBadge(score) {
  if (score === 0) return { label: 'Not Assessed', bg: '#f3f4f6', color: '#6b7280' };
  if (score <= 20) return { label: 'Not Started', bg: '#fee2e2', color: '#dc2626' };
  if (score <= 40) return { label: 'Initiating', bg: '#ffedd5', color: '#ea580c' };
  if (score <= 60) return { label: 'Developing', bg: '#fef3c7', color: '#d97706' };
  if (score <= 80) return { label: 'Established', bg: '#dbeafe', color: '#2563eb' };
  return { label: 'Optimized', bg: '#dcfce7', color: '#059669' };
}

function ArchitectureDiagram({ components, providerMeta, stepColor }) {
  if (!components || components.length === 0) return null;
  const boxW = 240, boxH = 80, gapX = 48, gapY = 32, padX = 30, padY = 30;
  const cols = Math.min(components.length, 3);
  const rows = Math.ceil(components.length / cols);
  const svgW = padX * 2 + cols * boxW + (cols - 1) * gapX;
  const svgH = padY * 2 + rows * boxH + (rows - 1) * gapY + 70;

  const colors = ['#7c3aed', '#2563eb', '#0891b2', '#d97706', '#dc2626', '#059669', '#4f46e5', '#9333ea'];

  return (
    <svg width="100%" viewBox={`0 0 ${svgW} ${svgH}`} style={{ maxWidth: '100%', borderRadius: 10 }}>
      <defs>
        <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
          <polygon points="0 0, 8 3, 0 6" fill="#94a3b8" />
        </marker>
        <filter id="shadow" x="-4%" y="-4%" width="108%" height="112%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.08" />
        </filter>
      </defs>
      <rect x="0" y="0" width={svgW} height={svgH} rx="12" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="2" />
      <rect x={svgW / 2 - 140} y="12" width="280" height="32" rx="6" fill="#1e1b4b" />
      <text x={svgW / 2} y="33" textAnchor="middle" fill="#fff" fontSize="12" fontWeight="700" fontFamily="system-ui">{providerMeta.shortLabel} Architecture — Key Components</text>

      {components.map((comp, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const x = padX + col * (boxW + gapX);
        const y = padY + 56 + row * (boxH + gapY);
        const c = colors[i % colors.length];
        const maxNameLen = 32;
        const maxPurpLen = 40;
        const maxSvcLen = 35;

        return (
          <g key={i}>
            <rect x={x} y={y} width={boxW} height={boxH} rx="8" fill="#fff" stroke={c} strokeWidth="1.5" filter="url(#shadow)" />
            <rect x={x} y={y} width={5} height={boxH} rx="2" fill={c} />
            <text x={x + 16} y={y + 22} fontSize="12" fontWeight="700" fill="#111827" fontFamily="system-ui">{comp.name?.length > maxNameLen ? comp.name.slice(0, maxNameLen - 1) + '…' : comp.name}</text>
            <text x={x + 16} y={y + 40} fontSize="10" fill="#6b7280" fontFamily="system-ui">{comp.purpose?.length > maxPurpLen ? comp.purpose.slice(0, maxPurpLen - 1) + '…' : comp.purpose}</text>
            {comp.service && (
              <g>
                <rect x={x + 16} y={y + 52} width={Math.min(comp.service.length * 6.2 + 14, boxW - 32)} height="18" rx="4" fill={providerMeta.bg} />
                <text x={x + 23} y={y + 65} fontSize="9.5" fill={providerMeta.color} fontWeight="600" fontFamily="system-ui">{comp.service.length > maxSvcLen ? comp.service.slice(0, maxSvcLen - 1) + '…' : comp.service}</text>
              </g>
            )}
            {i > 0 && row === Math.floor((i - 1) / cols) && (
              <line x1={x - gapX + 2} y1={y + boxH / 2} x2={x - 2} y2={y + boxH / 2} stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="4,3" markerEnd="url(#arrowhead)" />
            )}
            {row > 0 && col === 0 && (
              <line x1={x + boxW / 2} y1={y - gapY + 2} x2={x + boxW / 2} y2={y - 2} stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="4,3" markerEnd="url(#arrowhead)" />
            )}
          </g>
        );
      })}
    </svg>
  );
}

function StageDetailPanel({ step, stageIndex, answers, aiAnswers, onClose, cloudProvider, initialData, onDataGenerated }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [stageData, setStageData] = useState(initialData || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const provider = cloudProvider || 'azure';
  const providerMeta = PROVIDER_META[provider];

  const fetchRecommendation = async () => {
    setLoading(true);
    setError(null);
    try {
      const cloudVals = Object.values(answers || {});
      const overallScore = cloudVals.length > 0 ? Math.round((cloudVals.reduce((a, b) => a + b, 0) / (cloudVals.length * 5)) * 100) : 0;
      const res = await getStageRecommendation({
        stageIndex,
        stageName: step.label,
        workshopAnswers: answers || {},
        aiAnswers: aiAnswers || {},
        overallScore,
        cloudProvider: provider,
      });
      setStageData(res.data);
      if (onDataGenerated) onDataGenerated(stageIndex, res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate recommendations');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const renderOverview = () => {
    const ov = stageData?.overview;
    if (!ov) return <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Click "Generate AI Guidance" to generate implementation details for this stage.</p>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <p style={{ fontSize: '0.92rem', color: '#374151', lineHeight: 1.8, margin: 0 }}>{ov.description}</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
          <div style={{ background: '#f0f4ff', borderRadius: 12, padding: '1.5rem', border: '1px solid #c7d2fe' }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#4f46e5', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <FiCheckCircle size={16} /> Objectives
            </div>
            {(ov.objectives || []).map((o, i) => (
              <div key={i} style={{ fontSize: '0.88rem', color: '#374151', padding: '0.3rem 0', display: 'flex', gap: '0.5rem', lineHeight: 1.6 }}>
                <span style={{ color: '#4f46e5', flexShrink: 0, fontWeight: 700 }}>•</span> {o}
              </div>
            ))}
          </div>
          <div style={{ background: '#fffbeb', borderRadius: 12, padding: '1.5rem', border: '1px solid #fde68a' }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#d97706', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <FiAlertTriangle size={16} /> Prerequisites
            </div>
            {(ov.prerequisites || []).map((p, i) => (
              <div key={i} style={{ fontSize: '0.88rem', color: '#374151', padding: '0.3rem 0', display: 'flex', gap: '0.5rem', lineHeight: 1.6 }}>
                <span style={{ color: '#d97706', flexShrink: 0, fontWeight: 700 }}>•</span> {p}
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {ov.estimatedDuration && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: '#6b7280', background: '#f3f4f6', padding: '0.5rem 1rem', borderRadius: 8, fontWeight: 500 }}>
              <FiClock size={15} /> {ov.estimatedDuration}
            </div>
          )}
          {(ov.keyStakeholders || []).map((s, i) => (
            <div key={i} style={{ fontSize: '0.85rem', color: '#4f46e5', background: '#eef2ff', padding: '0.5rem 1rem', borderRadius: 8, fontWeight: 500 }}>
              {s}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderGuide = () => {
    const guide = stageData?.implementationGuide;
    if (!guide) return <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Click "Generate AI Guidance" to see a personalized implementation guide based on your assessment results.</p>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {guide.assessmentContext && (
          <div style={{ background: '#f0f4ff', borderRadius: 12, padding: '1.25rem', border: '1px solid #c7d2fe' }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#4f46e5', marginBottom: '0.5rem' }}>Assessment-Based Analysis</div>
            <p style={{ fontSize: '0.88rem', color: '#374151', lineHeight: 1.7, margin: 0 }}>{guide.assessmentContext}</p>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          {(guide.steps || []).map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 48, flexShrink: 0 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: step.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontWeight: 700, flexShrink: 0, zIndex: 1 }}>
                  {s.order || i + 1}
                </div>
                {i < (guide.steps || []).length - 1 && (
                  <div style={{ width: 2, flex: 1, background: `${step.color}30`, minHeight: 20 }} />
                )}
              </div>
              <div style={{ flex: 1, paddingBottom: '1.5rem', paddingLeft: '0.75rem' }}>
                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', marginBottom: '0.3rem' }}>{s.title}</div>
                <div style={{ fontSize: '0.88rem', color: '#374151', lineHeight: 1.7, marginBottom: '0.5rem' }}>{s.description}</div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {(s[providerMeta.serviceKey] || s.azureService || s.awsService || s.gcpService) && <span style={{ fontSize: '0.78rem', padding: '3px 10px', borderRadius: 6, background: providerMeta.bg, color: providerMeta.color, fontWeight: 600 }}>{s[providerMeta.serviceKey] || s.azureService || s.awsService || s.gcpService}</span>}
                  {s.bestPractice && <span style={{ fontSize: '0.78rem', padding: '3px 10px', borderRadius: 6, background: '#dcfce7', color: '#059669', fontWeight: 500 }}>{s.bestPractice}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
          {(guide.commonPitfalls || []).length > 0 && (
            <div style={{ background: '#fef2f2', borderRadius: 12, padding: '1.25rem', border: '1px solid #fecaca' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#dc2626', marginBottom: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <FiAlertTriangle size={15} /> Common Pitfalls
              </div>
              {guide.commonPitfalls.map((p, i) => (
                <div key={i} style={{ fontSize: '0.88rem', color: '#374151', padding: '0.3rem 0', lineHeight: 1.6 }}>• {p}</div>
              ))}
            </div>
          )}
          {(guide.successCriteria || []).length > 0 && (
            <div style={{ background: '#f0fdf4', borderRadius: 12, padding: '1.25rem', border: '1px solid #bbf7d0' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#059669', marginBottom: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <FiCheckCircle size={15} /> Success Criteria
              </div>
              {guide.successCriteria.map((c, i) => (
                <div key={i} style={{ fontSize: '0.88rem', color: '#374151', padding: '0.3rem 0', lineHeight: 1.6 }}>• {c}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderArchitecture = () => {
    const arch = stageData?.architectureReference;
    if (!arch) return <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Click "Generate AI Guidance" to see architecture references and diagrams.</p>;

    const diagramComponents = (arch.keyComponents || []).map(c => ({
      name: c.name,
      purpose: c.purpose,
      service: c[providerMeta.tagKey] || c.azureService || c.awsService || c.gcpService || '',
    }));

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <div style={{ background: providerMeta.bg + '40', borderRadius: 12, padding: '1.25rem', border: `1px solid ${providerMeta.bg}` }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: providerMeta.color, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <FiGlobe size={16} /> {providerMeta.label} Reference Architecture
          </div>
          <div style={{ fontSize: '0.9rem', color: '#374151', lineHeight: 1.7 }}>
            {arch.referenceArchitecture || arch.microsoftReference || arch.awsEquivalent || arch.gcpEquivalent}
          </div>
        </div>

        {diagramComponents.length > 0 && (
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '1rem', overflow: 'auto' }}>
            <ArchitectureDiagram components={diagramComponents} providerMeta={providerMeta} stepColor={step.color} />
          </div>
        )}

        {(arch.designDecisions || []).length > 0 && (
          <div style={{ background: '#f8fafc', borderRadius: 12, padding: '1.25rem', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#111827', marginBottom: '0.75rem' }}>Key Design Decisions</div>
            {arch.designDecisions.map((d, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.5rem', padding: '0.4rem 0', fontSize: '0.88rem', color: '#374151', lineHeight: 1.6 }}>
                <FiArrowRight size={14} style={{ flexShrink: 0, marginTop: 3, color: '#4f46e5' }} /> {d}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderTerraform = () => {
    const tf = stageData?.terraformAccelerator;
    if (!tf) return <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Click "Generate AI Guidance" to see Terraform accelerators.</p>;

    const fullCode = tf.fullCode || (tf.resources || []).map(r => r.codeSnippet).join('\n\n');

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <p style={{ fontSize: '0.9rem', color: '#374151', lineHeight: 1.7, margin: 0 }}>{tf.description}</p>
        {(tf.providers || []).length > 0 && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {tf.providers.map((p, i) => (
              <span key={i} style={{ fontSize: '0.78rem', padding: '4px 12px', borderRadius: 6, background: '#7c3aed15', color: '#7c3aed', fontWeight: 600, fontFamily: 'monospace' }}>{p}</span>
            ))}
          </div>
        )}
        <div style={{ borderRadius: 12, border: '1px solid #334155', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1.25rem', background: '#1e293b', borderBottom: '1px solid #334155' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FiCode size={15} color="#94a3b8" />
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#e2e8f0', fontFamily: 'monospace' }}>main.tf</span>
              <span style={{ fontSize: '0.75rem', color: '#94a3b8', marginLeft: '0.5rem' }}>{step.label} — {providerMeta.shortLabel}</span>
            </div>
            <button
              onClick={() => copyCode(fullCode)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.4rem 0.85rem', borderRadius: 6, border: '1px solid #475569', background: '#334155', color: copied ? '#34d399' : '#94a3b8', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 500, transition: 'all 0.2s' }}
            >
              {copied ? <><FiCheckCircle size={13} /> Copied!</> : <><FiClipboard size={13} /> Copy All</>}
            </button>
          </div>
          <pre style={{ margin: 0, padding: '1.5rem', background: '#0f172a', color: '#e2e8f0', fontSize: '0.82rem', lineHeight: 1.7, overflow: 'auto', maxHeight: 600, fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', monospace", tabSize: 2 }}>
            {fullCode}
          </pre>
        </div>
        {(tf.variables || []).length > 0 && (
          <div>
            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#111827', marginBottom: '0.6rem' }}>Configurable Variables</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.6rem' }}>
              {tf.variables.map((v, i) => (
                <div key={i} style={{ background: '#f9fafb', borderRadius: 10, padding: '0.85rem 1rem', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#7c3aed', fontFamily: 'monospace' }}>{v.name}</div>
                  <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 3, lineHeight: 1.4 }}>{v.description}</div>
                  <div style={{ display: 'flex', gap: '0.4rem', marginTop: 5 }}>
                    <span style={{ fontSize: '0.7rem', padding: '2px 6px', borderRadius: 4, background: '#f3f4f6', color: '#6b7280' }}>{v.type}</span>
                    {v.default && <span style={{ fontSize: '0.7rem', padding: '2px 6px', borderRadius: 4, background: '#f3f4f6', color: '#6b7280' }}>default: {v.default}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAI = () => {
    const ai = stageData?.aiRecommendations;
    if (!ai) return <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Click "Generate AI Guidance" to see personalized recommendations.</p>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <div style={{ background: '#f0f4ff', borderRadius: 12, padding: '1.25rem', border: '1px solid #c7d2fe' }}>
          <div style={{ fontSize: '0.9rem', color: '#374151', lineHeight: 1.8 }}>{ai.summary}</div>
        </div>
        {(ai.recommendations || []).map((r, i) => {
          const prioColor = r.priority === 'High' ? '#dc2626' : r.priority === 'Medium' ? '#d97706' : '#059669';
          const prioBg = r.priority === 'High' ? '#fee2e2' : r.priority === 'Medium' ? '#fef3c7' : '#dcfce7';
          return (
            <div key={i} style={{ display: 'flex', gap: '1rem', padding: '1.25rem', background: '#f9fafb', borderRadius: 12, border: '1px solid #e5e7eb' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '4px 10px', borderRadius: 6, background: prioBg, color: prioColor, flexShrink: 0, height: 'fit-content' }}>{r.priority}</span>
              <div>
                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', marginBottom: '0.3rem' }}>{r.title}</div>
                <div style={{ fontSize: '0.88rem', color: '#374151', lineHeight: 1.7 }}>{r.description}</div>
                {r.impact && <div style={{ fontSize: '0.82rem', color: '#4f46e5', marginTop: '0.4rem', fontWeight: 500 }}>Impact: {r.impact}</div>}
              </div>
            </div>
          );
        })}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
          {(ai.maturityGaps || []).length > 0 && (
            <div style={{ background: '#fffbeb', borderRadius: 12, padding: '1.25rem', border: '1px solid #fde68a' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#d97706', marginBottom: '0.6rem' }}>Maturity Gaps</div>
              {ai.maturityGaps.map((g, i) => <div key={i} style={{ fontSize: '0.88rem', color: '#374151', padding: '0.3rem 0', lineHeight: 1.6 }}>• {g}</div>)}
            </div>
          )}
          {(ai.quickWins || []).length > 0 && (
            <div style={{ background: '#f0fdf4', borderRadius: 12, padding: '1.25rem', border: '1px solid #bbf7d0' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#059669', marginBottom: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <FiZap size={15} /> Quick Wins
              </div>
              {ai.quickWins.map((w, i) => <div key={i} style={{ fontSize: '0.88rem', color: '#374151', padding: '0.3rem 0', lineHeight: 1.6 }}>• {w}</div>)}
            </div>
          )}
        </div>
      </div>
    );
  };

  const tabContent = {
    overview: renderOverview,
    guide: renderGuide,
    architecture: renderArchitecture,
    terraform: renderTerraform,
    ai: renderAI,
  };

  return (
    <div style={{ flex: 1, background: '#fff', borderRadius: 14, border: `2px solid ${step.color}20`, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 500 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.5rem', background: `${step.color}06`, borderBottom: `1px solid ${step.color}15` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: step.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <step.icon size={20} color="#fff" />
          </div>
          <div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#111827' }}>{step.label}</div>
            <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{providerMeta.label} — Stage {stageIndex + 1} of 8</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {!stageData && !loading && (
            <button
              onClick={fetchRecommendation}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1.25rem', borderRadius: 8, border: 'none', background: step.color, color: '#fff', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}
            >
              <FiCpu size={16} /> Generate AI Guidance
            </button>
          )}
          {stageData && !loading && (
            <button
              onClick={fetchRecommendation}
              style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.5rem 0.85rem', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 500 }}
            >
              <FiCpu size={14} /> Regenerate
            </button>
          )}
          <button onClick={onClose} style={{ display: 'flex', alignItems: 'center', padding: '0.4rem', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', cursor: 'pointer' }}>
            <FiX size={18} />
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', background: '#fafafa', overflowX: 'auto' }}>
        {TAB_DEFS.map((tab) => {
          const TabIcon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.35rem',
                padding: '0.75rem 1rem', border: 'none', cursor: 'pointer',
                fontSize: '0.8rem', fontWeight: isActive ? 700 : 500,
                color: isActive ? step.color : '#6b7280',
                background: isActive ? '#fff' : 'transparent',
                borderBottom: isActive ? `2px solid ${step.color}` : '2px solid transparent',
                transition: 'all 0.15s',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              <TabIcon size={14} /> {tab.label}
            </button>
          );
        })}
      </div>

      <div style={{ padding: '2rem', flex: 1, overflow: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <FiLoader size={36} color={step.color} style={{ animation: 'spin 1s linear infinite' }} />
            <p style={{ fontSize: '0.95rem', color: '#6b7280', marginTop: '1.25rem' }}>Generating {providerMeta.shortLabel} implementation guidance...</p>
            <p style={{ fontSize: '0.82rem', color: '#9ca3af', marginTop: '0.25rem' }}>Analyzing assessment results for {step.label}</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p style={{ fontSize: '0.9rem', color: '#dc2626', marginBottom: '1rem' }}>{error}</p>
            <button
              onClick={fetchRecommendation}
              style={{ padding: '0.6rem 1.25rem', borderRadius: 8, border: 'none', background: step.color, color: '#fff', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}
            >
              Retry
            </button>
          </div>
        ) : (
          tabContent[activeTab]()
        )}
      </div>
    </div>
  );
}

export default function LandingZoneArchitecturePreview({ answers, aiAnswers, cloudProvider, onStageDataChange }) {
  const [expandedStage, setExpandedStage] = useState(null);
  const [stageDataMap, setStageDataMap] = useState({});
  const hasAnswers = Object.keys(answers).length > 0;
  if (!hasAnswers) return null;

  const handleStageDataGenerated = (idx, data) => {
    const updated = { ...stageDataMap, [idx]: { stageName: FLOW_STEPS[idx].label, ...data } };
    setStageDataMap(updated);
    if (onStageDataChange) onStageDataChange(updated);
  };

  const provider = cloudProvider || 'azure';
  const providerMeta = PROVIDER_META[provider];

  return (
    <div style={{ marginTop: '2rem' }}>
      <div style={{ marginBottom: '1.5rem', borderTop: '2px solid #e5e7eb', paddingTop: '2rem' }}>
        <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#111827', margin: 0 }}>
          <FiLayers style={{ marginRight: 8, verticalAlign: 'middle', color: '#4f46e5' }} />
          Landing Zone Architecture Preview
        </h2>
        <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0.25rem 0 0' }}>
          {providerMeta.label} architecture view generated from Discovery Workshop inputs
        </p>
      </div>

      <div style={{ ...cardStyle, marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#111827', marginBottom: '1.25rem' }}>
          {providerMeta.shortLabel} Landing Zone Architecture Diagram
        </h3>

        <div style={{ position: 'relative' }}>
          <div style={{
            background: '#f8fafc', borderRadius: 12, border: '2px solid #e2e8f0',
            padding: '1.5rem', position: 'relative',
          }}>
            <div style={{
              textAlign: 'center', marginBottom: '1.5rem', padding: '0.75rem',
              background: '#1e1b4b', borderRadius: 10, color: '#fff',
            }}>
              <div style={{ fontSize: '1rem', fontWeight: 700 }}>{providerMeta.label} Landing Zone</div>
              <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>Enterprise-Scale Architecture</div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }}>
              <svg width="2" height="20"><line x1="1" y1="0" x2="1" y2="20" stroke="#94a3b8" strokeWidth="2" strokeDasharray="4,3" /></svg>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {ARCH_LAYERS.map((layer, idx) => {
                const score = getLayerMaturity(answers, layer);
                const badge = getMaturityBadge(score);
                const Icon = layer.icon;
                const barWidth = Math.max(score, 4);
                const layerItems = layer.items[provider] || layer.items.azure || [];

                return (
                  <React.Fragment key={layer.key}>
                    <div style={{
                      display: 'flex', alignItems: 'stretch', gap: '0',
                      borderRadius: 10, overflow: 'hidden',
                      border: `1px solid ${layer.border}`,
                      background: '#fff',
                    }}>
                      <div style={{
                        width: 44, minHeight: 70, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: layer.color, flexShrink: 0,
                      }}>
                        <Icon size={20} color="#fff" />
                      </div>

                      <div style={{ flex: 1, padding: '0.65rem 0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#111827' }}>{layer.label}</span>
                            <span style={{
                              fontSize: '0.6rem', fontWeight: 700, padding: '1px 7px', borderRadius: 8,
                              background: badge.bg, color: badge.color,
                            }}>
                              {badge.label}
                            </span>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                            {layerItems.map((item, ii) => (
                              <span key={ii} style={{
                                fontSize: '0.68rem', color: '#6b7280', padding: '1px 6px',
                                background: layer.bg, borderRadius: 4,
                              }}>
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div style={{ width: 90, flexShrink: 0 }}>
                          <div style={{ fontSize: '0.7rem', color: '#6b7280', textAlign: 'right', marginBottom: 3 }}>{score}%</div>
                          <div style={{ height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{
                              height: '100%', width: `${barWidth}%`, borderRadius: 3,
                              background: layer.color, transition: 'width 0.5s ease',
                            }} />
                          </div>
                        </div>
                      </div>
                    </div>

                    {idx < ARCH_LAYERS.length - 1 && (
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <svg width="2" height="14"><line x1="1" y1="0" x2="1" y2="14" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="3,3" /></svg>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ ...cardStyle, marginBottom: expandedStage !== null ? '1rem' : 0 }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#111827', marginBottom: '0.35rem' }}>
            Landing Zone Implementation Flow
          </h3>
          <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: expandedStage !== null ? 0 : '1.25rem' }}>
            Click any stage for {providerMeta.shortLabel}-specific implementation guidance, architecture diagrams, Terraform code, and AI recommendations
          </p>

          {expandedStage === null && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, maxWidth: 520, margin: '0 auto' }}>
                {FLOW_STEPS.map((step, idx) => {
                  const Icon = step.icon;
                  const isFirst = idx === 0;
                  const isLast = idx === FLOW_STEPS.length - 1;
                  return (
                    <React.Fragment key={idx}>
                      <div
                        onClick={() => setExpandedStage(idx)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.75rem',
                          width: '100%',
                          padding: '0.7rem 1rem', borderRadius: 10,
                          background: isFirst ? step.color : '#fff',
                          border: isFirst ? 'none' : `2px solid ${step.color}60`,
                          color: isFirst ? '#fff' : step.color,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                      >
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: isFirst ? 'rgba(255,255,255,0.2)' : `${step.color}15`,
                          flexShrink: 0,
                        }}>
                          <Icon size={16} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: 700 }}>{step.label}</div>
                          <div style={{ fontSize: '0.7rem', opacity: 0.75, fontWeight: 400, color: isFirst ? 'rgba(255,255,255,0.85)' : '#6b7280' }}>
                            {step.sublabel}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexShrink: 0 }}>
                          <div style={{
                            width: 24, height: 24, borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: isFirst ? 'rgba(255,255,255,0.2)' : `${step.color}15`,
                            fontSize: '0.7rem', fontWeight: 700,
                          }}>
                            {idx + 1}
                          </div>
                          <FiChevronDown size={16} />
                        </div>
                      </div>

                      {!isLast && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: 28 }}>
                          <svg width="2" height={18} style={{ marginTop: 2 }}>
                            <line x1="1" y1="0" x2="1" y2={18} stroke="#94a3b8" strokeWidth="2" strokeDasharray="4,3" />
                          </svg>
                          <FiArrowDown size={10} color="#94a3b8" />
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              <div style={{
                marginTop: '1rem', padding: '0.75rem 1rem', background: '#f0f4ff',
                borderRadius: 8, border: '1px solid #c7d2fe',
                fontSize: '0.82rem', color: '#374151', lineHeight: 1.6,
              }}>
                Each stage generates {providerMeta.shortLabel}-specific implementation guidance based on your workshop assessment results, including architecture diagrams, production-ready Terraform code, and personalized AI recommendations.
              </div>
            </>
          )}
        </div>

        {expandedStage !== null && (
          <div style={{
            display: 'flex', gap: '1rem', alignItems: 'flex-start',
          }}>
            <div style={{
              width: 220, minWidth: 220, flexShrink: 0,
              background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0',
              padding: '0.75rem',
              maxHeight: 'calc(100vh - 200px)', overflowY: 'auto',
            }}>
              {FLOW_STEPS.map((step, idx) => {
                const Icon = step.icon;
                const isFirst = idx === 0;
                const isLast = idx === FLOW_STEPS.length - 1;
                const isActive = expandedStage === idx;
                return (
                  <React.Fragment key={idx}>
                    <div
                      onClick={() => setExpandedStage(idx)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.4rem',
                        padding: '0.45rem 0.5rem', borderRadius: 8,
                        background: isActive ? (isFirst ? step.color : `${step.color}12`) : 'transparent',
                        border: isActive ? `2px solid ${step.color}` : '2px solid transparent',
                        color: isFirst && isActive ? '#fff' : step.color,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: isFirst && isActive ? 'rgba(255,255,255,0.2)' : `${step.color}15`,
                        flexShrink: 0,
                      }}>
                        <Icon size={12} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0, fontSize: '0.72rem', fontWeight: isActive ? 700 : 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{step.label}</div>
                      <div style={{
                        width: 18, height: 18, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: isFirst && isActive ? 'rgba(255,255,255,0.2)' : `${step.color}10`,
                        fontSize: '0.58rem', fontWeight: 700, flexShrink: 0,
                      }}>
                        {idx + 1}
                      </div>
                    </div>
                    {!isLast && (
                      <div style={{ display: 'flex', justifyContent: 'center', height: 12 }}>
                        <svg width="2" height={8} style={{ marginTop: 1 }}>
                          <line x1="1" y1="0" x2="1" y2={8} stroke="#cbd5e1" strokeWidth="2" strokeDasharray="3,2" />
                        </svg>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <StageDetailPanel
                step={FLOW_STEPS[expandedStage]}
                stageIndex={expandedStage}
                answers={answers}
                aiAnswers={aiAnswers}
                onClose={() => setExpandedStage(null)}
                cloudProvider={provider}
                initialData={stageDataMap[expandedStage] || null}
                onDataGenerated={handleStageDataGenerated}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
